import { createSelector } from "reselect";
import { type PriceData } from "../../models/PriceData";
import { volatileSum } from "../../utils/PriceUtils";
import { type RootState } from "../../redux/store";
import { type ResolvedWishlistEntryModel, type ResolvedWishlistModel, type WishlistEntryModel } from "../model";
import { type SelectNetPriceProps, selectSubtotalPrice } from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";
import { getCanUserAddToCart } from "~/ProductDetails/components/ProductSummary/ExtendedPdpSummary";
import { userIsRegistered } from "~/utils/roles";
import { type WishlistPageState } from "~/Wishlist/redux/types";

export const getWishlists = createSelector(
  (state: WishlistPageState) => state.wishlistIds,
  (state: WishlistPageState) => state.wishlists,
  (ids, wishlistsById) => ids.map((id) => wishlistsById[id])
);

export const selectIsWishlistsLoading = (state: RootState) => state.wishlistPage.loading || !state.wishlistPage.loaded;

export const selectSelectedWishlistId = (state: RootState) => state.wishlistPage.selectedWishlistId;
export const selectWishlists = (state: RootState) => getWishlists(state.wishlistPage);

export const selectedSelectedWishlist = createSelector(
  selectSelectedWishlistId,
  (state: RootState) => state.wishlistPage.wishlists,
  (wishlistId, wishlists) => {
    if (!wishlistId) {
      return null;
    }
    return wishlists[wishlistId];
  }
);

export const selectSelectedWishlistEntries = createSelector(
  selectedSelectedWishlist,
  (state: RootState) => state.wishlistPage.entries,
  (wishlist, entriesById) => {
    if (!wishlist) {
      return [];
    }
    return wishlist.entryIds.map((id) => entriesById[id]);
  }
);

export const selectWishlistSize = (wishlistId: string) => (state: RootState) => {
  return state.wishlistPage.wishlists[wishlistId]?.entryIds.length ?? 0;
};

interface PriceEntry {
  perPiece: PriceData;
  total: PriceData;
}

type PriceEntryMap = { [productCode: string]: PriceEntry };

export const isAllWishlistDetailsLoaded = createSelector(
  (state: RootState) => state.products.price,
  (priceMap) => {
    return Object.values(priceMap).every((entry) => entry.state === "loaded");
  }
);

const selectWishlistEntry = (wishlistEntryId: string) => (state: RootState) =>
  state.wishlistPage.entries[wishlistEntryId];

export const getSelectPriceParamsFromWishlistEntry = (
  wishlistEntry: WishlistEntryModel | ResolvedWishlistEntryModel
): Required<SelectNetPriceProps> => {
  return {
    packCode: "", // wishlist does not have a pack code
    extraQuantity: wishlistEntry.quantity,
    priceReference: wishlistEntry.priceReference,
    productCode: "productCode" in wishlistEntry ? wishlistEntry.productCode : wishlistEntry.product.base.code,
  };
};

const getSelectPriceParamsFromWishlistEntryParts = (
  wishlistEntry: WishlistEntryModel
): Required<SelectNetPriceProps>[] => {
  return wishlistEntry.optionalParts.map((part) => ({
    packCode: "", // wishlist does not have a pack code
    extraQuantity: part.quantity,
    priceReference: wishlistEntry.priceReference,
    productCode: part.productCode,
  }));
};

export const getSubtotalSelectPriceFromWishlistEntry = (
  wishlistEntry: WishlistEntryModel
): Required<SelectNetPriceProps>[] => {
  return [
    getSelectPriceParamsFromWishlistEntry(wishlistEntry),
    ...getSelectPriceParamsFromWishlistEntryParts(wishlistEntry),
  ];
};

export const selectSubtotalForWishlistEntry = (wishlistEntryId: string) => (state: RootState) => {
  const entry = selectWishlistEntry(wishlistEntryId)(state);

  return selectSubtotalPrice(getSubtotalSelectPriceFromWishlistEntry(entry))(state);
};

export const selectTotalWishlistValue = (wishlistId: string) => (state: RootState) => {
  const wishlist = state.wishlistPage.wishlists[wishlistId];

  return volatileSum(...wishlist.entryIds.map((entryId) => selectSubtotalForWishlistEntry(entryId)(state)));
};

export const selectWishlistAmount = (state: RootState) =>
  state.wishlistPage.loaded ? state.wishlistPage.wishlistIds.length : undefined;

export const selectFullWishlist =
  (wishlistId?: string) =>
  (state: RootState): ResolvedWishlistModel | null => {
    if (!wishlistId) {
      return null;
    }

    const wishlistModel = state.wishlistPage.wishlists[wishlistId];

    if (!wishlistModel) {
      return null;
    }

    return {
      ...wishlistModel,
      entries: wishlistModel.entryIds.map((id) => {
        const entry = state.wishlistPage.entries[id];
        const product = selectProductDescriptor(state, entry);
        return {
          ...entry,
          product,
          isSelectable: userIsRegistered()
            ? true
            : product.isLoaded && getCanUserAddToCart(product, product?.availability?.packagingInformation[0]),
        };
      }),
    };
  };
